// The walk time bottom sheet. Spec 7.1.
//
// Settings screens go unvisited, so the important entry point is not the
// settings screen: it is tapping the number itself inside a plan card,
// standing on a concourse at the moment you notice it is wrong.
import { useEffect, useState } from 'react';
import {
  clampSeconds,
  confidenceOf,
  driftPrompt,
  segmentSeconds,
  type Profile,
  type WalkSegment,
} from '../../shared/segments';

const mmss = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${String(Math.round(seconds % 60)).padStart(2, '0')}`;

function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return <div className="sparkline-empty">No walks timed yet</div>;
  const max = Math.max(...values);
  const min = Math.min(...values);
  const span = max - min || 1;
  return (
    <div className="sparkline" title={values.map((v) => mmss(v)).join(', ')}>
      {values.map((v, i) => (
        <span key={i} style={{ height: `${8 + ((v - min) / span) * 28}px` }} />
      ))}
      <em>last {values.length} walks</em>
    </div>
  );
}

export function WalkTimeSheet({
  segment,
  profile,
  onChange,
  onClose,
}: {
  segment: WalkSegment;
  profile: Profile;
  onChange: (s: WalkSegment) => void;
  onClose: () => void;
}) {
  const [seconds, setSeconds] = useState(() =>
    Math.round(segmentSeconds(segment, new Date(), { ...profile, paceFactor: 1 }))
  );
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [now, setNow] = useState(Date.now());

  // While timing, wake up once a second and re-read the clock. We never
  // count intervals, because the PWA gets suspended the second the screen
  // locks and an interval counter would quietly lose the walk. Spec 7.1.
  useEffect(() => {
    if (!startedAt) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [startedAt]);

  // Keep the screen awake while timing, and fall back gracefully when the
  // API is missing, which it is on most of iOS.
  useEffect(() => {
    if (!startedAt) return;
    let lock: { release: () => Promise<void> } | null = null;
    const wl = (navigator as Navigator & { wakeLock?: { request: (t: string) => Promise<never> } })
      .wakeLock;
    wl?.request('screen')
      .then((l) => {
        lock = l;
      })
      .catch(() => undefined);
    return () => void lock?.release().catch(() => undefined);
  }, [startedAt]);

  // Saving is immediate. No confirm button. The plan list behind the sheet
  // recalculates live as you adjust. Spec 7.1.
  const nudge = (delta: number) => {
    const next = clampSeconds(seconds + delta);
    setSeconds(next);
    onChange({ ...segment, overrideSeconds: next });
  };

  const stopTiming = () => {
    const measured = clampSeconds((Date.now() - startedAt!) / 1000);
    setStartedAt(null);
    onChange({ ...segment, samples: [...segment.samples, measured].slice(-7) });
  };

  const drift = driftPrompt(segment);

  if (startedAt) {
    const elapsed = Math.round((now - startedAt) / 1000);
    return (
      <div className="sheet-backdrop" onClick={onClose}>
        <div className="sheet" onClick={(e) => e.stopPropagation()}>
          <h2>{segment.label}</h2>
          <div className="bigclock">{mmss(elapsed)}</div>
          <button className="primary big" onClick={stopTiming}>
            Stop
          </button>
          <button className="ghost" onClick={() => setStartedAt(null)}>
            Discard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2>{segment.label}</h2>

        <div className="stepper">
          <button onClick={() => nudge(-30)} aria-label="Half a minute less">
            −
          </button>
          <span className="stepper-value">{mmss(seconds)}</span>
          <button onClick={() => nudge(30)} aria-label="Half a minute more">
            +
          </button>
        </div>

        <Sparkline values={segment.samples} />

        {drift && (
          <div className="drift">
            Your last few walks here took {mmss(drift.measured)}, not {mmss(segment.overrideSeconds!)}.
            <button
              className="link"
              onClick={() => {
                setSeconds(Math.round(drift.measured));
                onChange({ ...segment, overrideSeconds: Math.round(drift.measured) });
              }}
            >
              Update
            </button>
          </div>
        )}

        <button className="primary" onClick={() => setStartedAt(Date.now())}>
          Time this walk
        </button>
        <button
          className="ghost"
          onClick={() => {
            const cleared = { ...segment, overrideSeconds: undefined };
            setSeconds(Math.round(segmentSeconds(cleared, new Date(), { ...profile, paceFactor: 1 })));
            onChange(cleared);
          }}
        >
          Reset to default
        </button>

        <div className="confidence-note">
          {confidenceOf(segment) === 'seed'
            ? 'This is our guess, not your number. Tap the stepper or time a walk.'
            : confidenceOf(segment) === 'learning'
              ? `Learning: ${segment.samples.length} walk${segment.samples.length === 1 ? '' : 's'} recorded, 3 needed.`
              : 'Settled.'}
        </div>
      </div>
    </div>
  );
}
